import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Book, Mail, Settings, CreditCard, Zap, Globe, HelpCircle, Search, Lightbulb, Headphones, Bug, MessageSquare, Send } from 'lucide-react';

const Help = () => {
  const { isDark } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('getting-started');
  const [openArticles, setOpenArticles] = useState({});

  const toggleArticle = (id) => {
    setOpenArticles((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const sections = {
    'getting-started': {
      title: 'Getting Started',
      icon: Zap,
      articles: [
        {
          title: 'Quick Start Guide (2 Minutes)',
          content: `
            <h3 class="font-bold text-lg mb-2">Welcome to OffTrigger! 🎉</h3>
            <p class="mb-4">Get started in just 2 minutes:</p>
            
            <h4 class="font-bold mt-4 mb-2">Step 1: Connect Your Email</h4>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Go to <strong>Channels</strong> page</li>
              <li>Click <strong>"Connect Email"</strong></li>
              <li>Choose your email provider or use "Other" for any email</li>
              <li>Enter your credentials (see detailed instructions below)</li>
              <li>Click <strong>"Connect"</strong></li>
            </ol>
            
            <h4 class="font-bold mt-4 mb-2">Step 2: Customize Your Message</h4>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Go to <strong>Templates</strong> page</li>
              <li>Edit the auto-response message</li>
              <li>Add your personal touch, branding, contact info</li>
              <li>Click <strong>"Save"</strong></li>
            </ol>
            
            <h4 class="font-bold mt-4 mb-2">Step 3: Test It!</h4>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Send a test email to your connected inbox</li>
              <li>Include keywords like "inquiry" or "interested in property"</li>
              <li>Wait 10-30 seconds</li>
              <li>Check your Dashboard - the lead should appear!</li>
              <li>Check the test email inbox - auto-response should arrive!</li>
            </ol>
            
            <div class="p-4 rounded-lg bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400 mt-4">
              ✅ You're all set! OffTrigger will now monitor your inbox 24/7 and respond to leads in 30 seconds.
            </div>
          `
        },
        {
          title: 'How OffTrigger Works',
          content: `
            <h3 class="font-bold text-lg mb-2">Behind the Scenes</h3>
            
            <h4 class="font-bold mt-4 mb-2">1. Monitoring (Every 10 Seconds)</h4>
            <p class="mb-4">OffTrigger checks your inbox every 10 seconds for new emails from real estate platforms.</p>
            
            <h4 class="font-bold mt-4 mb-2">2. Smart Detection</h4>
            <p class="mb-2">Our system recognizes emails from 35+ platforms including:</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>🇺🇸 USA: Zillow, Redfin, Realtor.com, Trulia, Homes.com</li>
              <li>🇬🇧 UK: Rightmove, Zoopla, OnTheMarket</li>
              <li>🇮🇳 India: 99acres, MagicBricks, Housing.com</li>
              <li>🇦🇪 UAE: Property Finder, Bayut, Dubizzle</li>
              <li>🇦🇺 Australia: Domain, Realestate.com.au</li>
              <li>And many more!</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">3. Information Extraction</h4>
            <p class="mb-2">We extract:</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Lead name</li>
              <li>Contact info (email, phone)</li>
              <li>Property address</li>
              <li>Message content</li>
              <li>Platform source</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">4. Instant Response</h4>
            <p class="mb-2">Within 30 seconds, we send a personalized auto-response via:</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>📧 Email (always)</li>
              <li>📱 WhatsApp (if phone number available, international)</li>
              <li>💬 SMS (if phone number available, US)</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">5. You Take Over</h4>
            <p class="mb-4">The lead appears in your Dashboard. You follow up personally when ready!</p>
          `
        }
      ]
    },
    'email-connection': {
      title: 'Email Connection',
      icon: Mail,
      articles: [
        {
          title: 'Connecting Gmail',
          content: `
            <h3 class="font-bold text-lg mb-2">How to Connect Gmail</h3>
            
            <div class="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-600 dark:text-yellow-400 mb-4">
              ⚠️ Important: You need to create an "App Password" - your regular Gmail password won't work!
            </div>
            
            <h4 class="font-bold mt-4 mb-2">Step 1: Enable 2-Step Verification</h4>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Go to <a href="https://myaccount.google.com/security" target="_blank" class="text-blue-500 hover:underline">Google Account Security</a></li>
              <li>Find "2-Step Verification"</li>
              <li>Click "Get Started" and follow the steps</li>
              <li>Complete setup (usually takes 2-3 minutes)</li>
            </ol>
            
            <h4 class="font-bold mt-4 mb-2">Step 2: Create App Password</h4>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" class="text-blue-500 hover:underline">Google App Passwords</a></li>
              <li>Sign in if prompted</li>
              <li>Under "Select app" choose <strong>Mail</strong></li>
              <li>Under "Select device" choose <strong>Other (custom name)</strong></li>
              <li>Type: <strong>OffTrigger</strong></li>
              <li>Click <strong>Generate</strong></li>
              <li>Copy the 16-character password (looks like: <code>abcd efgh ijkl mnop</code>)</li>
            </ol>
            
            <h4 class="font-bold mt-4 mb-2">Step 3: Connect in OffTrigger</h4>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Go to <strong>Channels</strong> page</li>
              <li>Click <strong>"Connect Email"</strong></li>
              <li>Enter your Gmail address: <code>yourname@gmail.com</code></li>
              <li>Paste the App Password (the 16-character one, not your regular password!)</li>
              <li>Provider: Select <strong>Gmail</strong> or leave as Auto-detect</li>
              <li>Click <strong>"Connect"</strong></li>
            </ol>
            
             <div class="p-4 rounded-lg bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400 mt-4 mb-4">
              ✅ Connected! OffTrigger will now monitor your Gmail inbox.
            </div>
            
            <h4 class="font-bold mt-4 mb-2">Troubleshooting Gmail</h4>
            <p class="font-semibold mt-2">Error: "Authentication failed"</p>
            <ul class="list-disc pl-5 space-y-1 mb-2">
              <li>Make sure you're using the App Password, not your regular password</li>
              <li>Copy the App Password carefully (remove any spaces)</li>
              <li>Ensure 2-Step Verification is enabled</li>
            </ul>
            
            <p class="font-semibold mt-2">Error: "Connection timeout"</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Check your internet connection</li>
              <li>Try again in a few minutes</li>
              <li>Gmail servers might be temporarily down</li>
            </ul>
          `
        },
        {
          title: 'Connecting Outlook / Office 365',
          content: `
            <h3 class="font-bold text-lg mb-2">How to Connect Outlook</h3>
            
            <h4 class="font-bold mt-4 mb-2">For Outlook.com / Hotmail / Live.com:</h4>
            
            <p class="font-semibold mt-2">Step 1: Enable IMAP</p>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Sign in to <a href="https://outlook.live.com" target="_blank" class="text-blue-500 hover:underline">Outlook.com</a></li>
              <li>Click Settings (gear icon) → View all Outlook settings</li>
              <li>Go to Mail → Sync email</li>
              <li>Under "POP and IMAP", ensure IMAP is enabled</li>
              <li>Click "Save"</li>
            </ol>
            
            <p class="font-semibold mt-2">Step 2: Get App Password</p>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Go to <a href="https://account.microsoft.com/security" target="_blank" class="text-blue-500 hover:underline">Microsoft Security</a></li>
              <li>Click "Advanced security options"</li>
              <li>Scroll to "App passwords"</li>
              <li>Click "Create a new app password"</li>
              <li>Copy the generated password</li>
            </ol>
            
            <p class="font-semibold mt-2">Step 3: Connect in OffTrigger</p>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Go to Channels → Connect Email</li>
              <li>Email: <code>yourname@outlook.com</code></li>
              <li>Password: Paste the app password</li>
              <li>Provider: Select <strong>Outlook</strong></li>
              <li>Click "Connect"</li>
            </ol>
            
            <h4 class="font-bold mt-4 mb-2">For Office 365 / Work Email:</h4>
            <p class="mb-4">Same process, but check with your IT admin if IMAP is enabled for your organization.</p>
          `
        },
        {
          title: 'Connecting Other Email Providers',
          content: `
            <h3 class="font-bold text-lg mb-2">Connect Any Email Provider</h3>
            
            <p class="mb-4">OffTrigger works with <strong>any email provider</strong> that supports IMAP!</p>
            
            <h4 class="font-bold mt-4 mb-2">Common Providers:</h4>
            
            <p class="font-semibold mt-2">Yahoo Mail:</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>IMAP Server: <code>imap.mail.yahoo.com</code></li>
              <li>Port: <code>993</code></li>
              <li>Requires: App password (not regular password)</li>
              <li>Get app password: <a href="https://login.yahoo.com/account/security" target="_blank" class="text-blue-500 hover:underline">Yahoo Security Settings</a></li>
            </ul>
            
            <p class="font-semibold mt-2">iCloud Mail:</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>IMAP Server: <code>imap.mail.me.com</code></li>
              <li>Port: <code>993</code></li>
              <li>Requires: App-specific password</li>
              <li>Get password: <a href="https://appleid.apple.com" target="_blank" class="text-blue-500 hover:underline">Apple ID → Security</a></li>
            </ul>
            
            <p class="font-semibold mt-2">AOL Mail:</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>IMAP Server: <code>imap.aol.com</code></li>
              <li>Port: <code>993</code></li>
              <li>Requires: App password</li>
            </ul>
            
            <p class="font-semibold mt-2">Custom Domain / Other:</p>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Contact your email provider for IMAP settings</li>
              <li>You'll need:
                <ul class="list-disc pl-5 space-y-1 mt-1">
                  <li>IMAP server address (e.g., mail.yourdomain.com)</li>
                  <li>Port (usually 993 or 143)</li>
                  <li>Your email and password</li>
                </ul>
              </li>
              <li>In OffTrigger:
                <ul class="list-disc pl-5 space-y-1 mt-1">
                  <li>Select "Other" as provider</li>
                  <li>Enter custom IMAP server and port</li>
                  <li>Connect!</li>
                </ul>
              </li>
            </ol>
            
            <div class="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 mt-4">
              💡 <strong>Tip:</strong> Most corporate emails (Google Workspace, Microsoft 365) work the same way as Gmail/Outlook but check with your IT department first.
            </div>
          `
        },
        {
          title: 'Connection Expired - How to Reconnect',
          content: `
            <h3 class="font-bold text-lg mb-2">When Email Connection Expires</h3>
            
            <h4 class="font-bold mt-4 mb-2">Why Does This Happen?</h4>
            <p class="mb-2">Email providers occasionally require you to reconnect for security reasons:</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Password changed</li>
              <li>App password revoked</li>
              <li>Security settings updated</li>
              <li>Provider required re-authentication</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">How to Reconnect:</h4>
            
            <p class="font-semibold mt-2">You'll see a warning banner:</p>
            <div class="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-600 dark:text-yellow-400 mb-4 mt-2">
              ⚠️ Your email connection has expired. Reconnect to continue receiving leads.
            </div>
            
            <p class="font-semibold mt-2">Steps to fix:</p>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Go to <strong>Channels</strong> page</li>
              <li>Find the expired connection (shows red warning)</li>
              <li>Click <strong>"Reconnect"</strong></li>
              <li>Re-enter your credentials (may need new app password)</li>
              <li>Click <strong>"Connect"</strong></li>
              <li>Connection restored! ✅</li>
            </ol>
            
            <div class="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 mt-4">
              💡 Don't worry - your previous leads and settings are safe. Just reconnect and everything continues working!
            </div>
          `
        }
      ]
    },
    'features': {
      title: 'Features & Usage',
      icon: Settings,
      articles: [
        {
          title: 'Lead Scoring (Hot/Warm/Cold)',
          content: `
            <h3 class="font-bold text-lg mb-2">Understanding Lead Scores</h3>
            
            <p class="mb-4">OffTrigger automatically scores every lead to help you prioritize:</p>
            
            <h4 class="font-bold text-red-500 mt-4 mb-2">🔥 Hot Leads (70+ points)</h4>
            <ul class="list-disc pl-5 space-y-1 mb-2">
              <li>Received within last hour</li>
              <li>Has complete contact info (email + phone)</li>
              <li>From high-quality platforms</li>
              <li>Contains urgent keywords ("today", "ASAP")</li>
            </ul>
            <p class="mb-4"><strong>Action:</strong> Follow up immediately!</p>
            
            <h4 class="font-bold text-orange-500 mt-4 mb-2">⚡ Warm Leads (40-69 points)</h4>
            <ul class="list-disc pl-5 space-y-1 mb-2">
              <li>Received 1-24 hours ago</li>
              <li>Has email or phone (not both)</li>
              <li>Standard inquiry</li>
            </ul>
            <p class="mb-4"><strong>Action:</strong> Follow up within 24 hours</p>
            
            <h4 class="font-bold text-blue-500 mt-4 mb-2">❄️ Cold Leads (&lt;40 points)</h4>
            <ul class="list-disc pl-5 space-y-1 mb-2">
              <li>Older than 24 hours</li>
              <li>Incomplete contact info</li>
              <li>Generic inquiry</li>
            </ul>
            <p class="mb-4"><strong>Action:</strong> Follow up when you have time</p>
            
            <h4 class="font-bold mt-4 mb-2">How Scoring Works:</h4>
            <table class="min-w-full text-left border-collapse mt-2 mb-4">
              <thead>
                <tr><th class="border-b py-2 pr-4">Factor</th><th class="border-b py-2">Points</th></tr>
              </thead>
              <tbody>
                <tr><td class="py-1 pr-4">Last hour</td><td class="py-1 text-green-500">+30</td></tr>
                <tr><td class="py-1 pr-4">Last 24 hours</td><td class="py-1 text-green-500">+20</td></tr>
                <tr><td class="py-1 pr-4">Has email</td><td class="py-1 text-green-500">+15</td></tr>
                <tr><td class="py-1 pr-4">Has phone</td><td class="py-1 text-green-500">+15</td></tr>
                <tr><td class="py-1 pr-4">Premium platform</td><td class="py-1 text-green-500">+10</td></tr>
                <tr><td class="py-1 pr-4">Complete property info</td><td class="py-1 text-green-500">+10</td></tr>
              </tbody>
            </table>
          `
        },
        {
          title: 'Customizing Auto-Response Templates',
          content: `
            <h3 class="font-bold text-lg mb-2">Make It Personal</h3>
            
            <h4 class="font-bold mt-4 mb-2">Edit Your Template:</h4>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Go to <strong>Templates</strong> page</li>
              <li>Click <strong>"Edit Template"</strong></li>
              <li>Customize your message</li>
              <li>Use placeholders for personalization</li>
              <li>Click <strong>"Save"</strong></li>
            </ol>
            
            <h4 class="font-bold mt-4 mb-2">Available Placeholders:</h4>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li><code>{leadName}</code> - Lead's name</li>
              <li><code>{propertyAddress}</code> - Property they inquired about</li>
              <li><code>{agentName}</code> - Your name</li>
              <li><code>{date}</code> - Current date</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">Example Template:</h4>
            <pre class="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto text-sm mb-4">
Hi {leadName}!

Thank you for your inquiry about {propertyAddress}!

I received your message and I'm excited to help you. I'll follow up personally within the next few hours with more details about the property, pricing, and viewing options.

In the meantime, feel free to reply to this email with any questions!

Best regards,
{agentName}
Your Trusted Real Estate Agent
📞 (555) 123-4567
🌐 www.youragency.com
            </pre>
            
            <h4 class="font-bold mt-4 mb-2">Best Practices:</h4>
            <ul class="list-none space-y-1 mb-4">
              <li>✅ Keep it personal and friendly</li>
              <li>✅ Set clear expectations ("within 2 hours")</li>
              <li>✅ Include your contact info</li>
              <li>✅ Add value (link to virtual tour, neighborhood info)</li>
              <li>❌ Don't make it sound robotic</li>
              <li>❌ Don't make promises you can't keep</li>
            </ul>
          `
        },
        {
          title: 'Adding Notes to Leads',
          content: `
            <h3 class="font-bold text-lg mb-2">Track Your Follow-Ups</h3>
            
            <p class="mb-4">Keep all lead information in one place:</p>
            
            <h4 class="font-bold mt-4 mb-2">How to Add Notes:</h4>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Click on any lead in your Dashboard</li>
              <li>Lead details modal opens</li>
              <li>Scroll to "Notes" section</li>
              <li>Type your note</li>
              <li>Click "Add Note"</li>
            </ol>
            
            <h4 class="font-bold mt-4 mb-2">What to Track:</h4>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>📞 Phone call outcomes</li>
              <li>📅 Scheduled viewings</li>
              <li>💰 Budget discussed</li>
              <li>📝 Special requirements</li>
              <li>🏠 Properties shown</li>
              <li>✅ Next steps</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">Example Notes:</h4>
            <pre class="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto text-sm mb-4">
✅ Called lead - very interested
📅 Scheduled viewing for March 15 at 2 PM
💰 Budget: $500K-600K
📝 Looking for 3BR with garage
🔔 Follow up after viewing
            </pre>
          `
        },
        {
          title: 'Exporting Leads to CSV',
          content: `
            <h3 class="font-bold text-lg mb-2">Export Your Data</h3>
            
            <h4 class="font-bold mt-4 mb-2">How to Export:</h4>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Go to <strong>Dashboard</strong></li>
              <li>Click <strong>"Export CSV"</strong> button (top right)</li>
              <li>File downloads automatically</li>
              <li>Opens in Excel, Google Sheets, etc.</li>
            </ol>
            
            <h4 class="font-bold mt-4 mb-2">What's Included:</h4>
            <p class="mb-2">Your CSV contains:</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Lead name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Property address</li>
              <li>Platform source</li>
              <li>Lead score</li>
              <li>Date received</li>
              <li>Response time</li>
              <li>Status</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">Use Cases:</h4>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Import into your CRM</li>
              <li>Create mailing lists</li>
              <li>Monthly reporting</li>
              <li>Backup your data</li>
              <li>Share with team members</li>
            </ul>
          `
        },
        {
          title: 'Response Time Analytics',
          content: `
            <h3 class="font-bold text-lg mb-2">Track Your Performance</h3>
            
            <p class="mb-4">Your Dashboard shows:</p>
            
            <h4 class="font-bold mt-4 mb-2">Key Metrics:</h4>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li><strong>Average Response Time:</strong> Usually 20-30 seconds</li>
              <li><strong>Industry Average:</strong> 47 minutes (you're 97% faster!)</li>
              <li><strong>Total Leads:</strong> This month + all time</li>
              <li><strong>Money Saved:</strong> Based on $12K avg commission × leads saved</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">What These Numbers Mean:</h4>
            
            <p class="mb-4"><strong>30-second response time</strong> = You're responding 94X faster than the average agent</p>
            
            <p class="font-semibold mt-2">Example:</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>You received 20 leads this month</li>
              <li>Industry stats: 78% of leads move on within 1 hour</li>
              <li>Without OffTrigger: You'd lose ~15 leads</li>
              <li>Average commission: $12,000</li>
              <li>Money saved: 15 × $12,000 = $180,000 potential!</li>
            </ul>
          `
        }
      ]
    },
    'billing': {
      title: 'Billing & Plans',
      icon: CreditCard,
      articles: [
        {
          title: 'Free Trial (14 Days)',
          content: `
            <h3 class="font-bold text-lg mb-2">Your Free Trial</h3>
            
            <h4 class="font-bold mt-4 mb-2">What You Get:</h4>
            <ul class="list-none space-y-1 mb-4">
              <li>✅ Full access to all features</li>
              <li>✅ Unlimited leads</li>
              <li>✅ All channels (Email, WhatsApp, SMS)</li>
              <li>✅ 14 days completely free</li>
              <li>✅ No credit card required</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">Trial Timeline:</h4>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li><strong>Day 1:</strong> Sign up, connect email, start receiving leads</li>
              <li><strong>Day 7:</strong> Hopefully you've seen the value!</li>
              <li><strong>Day 13:</strong> We'll send a reminder (trial ends in 24 hours)</li>
              <li><strong>Day 14:</strong> Trial ends - upgrade to continue</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">What Happens After Trial?</h4>
            <p class="mb-2">If you don't upgrade:</p>
            <ul class="list-none space-y-1 mb-4">
              <li>❌ Auto-responses stop</li>
              <li>✅ You can still view your leads (read-only)</li>
              <li>✅ All your data is safe</li>
              <li>✅ You can export leads as CSV</li>
              <li>✅ You can upgrade anytime to resume</li>
            </ul>
            
            <div class="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 mt-4">
              💡 We don't delete your data! Upgrade anytime to pick up where you left off.
            </div>
          `
        },
        {
          title: 'Choosing the Right Plan',
          content: `
            <h3 class="font-bold text-lg mb-2">Which Plan Is Right for You?</h3>
            
            <h4 class="font-bold mt-4 mb-1 text-green-500">🟢 Starter ($49/month)</h4>
            <p class="text-sm mb-2 text-gray-500"><strong>Best for:</strong> Solo agents getting started</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Up to 100 leads/month</li>
              <li>1 email connection</li>
              <li>All response channels</li>
              <li>Lead scoring</li>
              <li>Basic support</li>
            </ul>
            <p class="mb-4 text-sm font-semibold"><strong>Choose if:</strong> You get 2-3 leads per day</p>
            
            <h4 class="font-bold mt-4 mb-1 text-blue-500">🔵 Professional ($89/month) ⭐ Most Popular</h4>
            <p class="text-sm mb-2 text-gray-500"><strong>Best for:</strong> Active agents</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Up to 300 leads/month</li>
              <li>3 email connections</li>
              <li>All Starter features</li>
              <li>Priority support</li>
              <li>Advanced analytics</li>
            </ul>
            <p class="mb-4 text-sm font-semibold"><strong>Choose if:</strong> You get 5-10 leads per day</p>
            
            <h4 class="font-bold mt-4 mb-1 text-purple-500">🟣 Team ($199/month)</h4>
            <p class="text-sm mb-2 text-gray-500"><strong>Best for:</strong> Teams & agencies</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Unlimited leads</li>
              <li>Unlimited email connections</li>
              <li>All Professional features</li>
              <li>Team collaboration</li>
              <li>Dedicated support</li>
              <li>Custom integrations</li>
            </ul>
            <p class="mb-4 text-sm font-semibold"><strong>Choose if:</strong> Multiple agents or 10+ leads per day</p>
            
            <h4 class="font-bold mt-4 mb-2 text-yellow-500">💰 Save 20% with Annual Billing</h4>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Starter: $468/year ($39/month)</li>
              <li>Professional: $852/year ($71/month)</li>
              <li>Team: $1,908/year ($159/month)</li>
            </ul>
          `
        },
        {
          title: 'How to Upgrade',
          content: `
            <h3 class="font-bold text-lg mb-2">Upgrade Your Plan</h3>
            
            <h4 class="font-bold mt-4 mb-2">Steps:</h4>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Go to <strong>Billing</strong> page</li>
              <li>Choose your plan (monthly or annual)</li>
              <li>Click <strong>"Upgrade"</strong></li>
              <li>Enter payment details</li>
              <li>Complete checkout</li>
              <li>You're upgraded! 🎉</li>
            </ol>
            
            <h4 class="font-bold mt-4 mb-2">Payment Methods:</h4>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>💳 Credit/Debit cards (Visa, Mastercard, Amex)</li>
              <li>🏦 Bank transfers (for annual plans)</li>
              <li>💰 UPI (India)</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">After Upgrade:</h4>
            <ul class="list-none space-y-1 mb-4">
              <li>✅ Instant activation (no waiting)</li>
              <li>✅ Trial period ends, billing begins</li>
              <li>✅ Receipt sent to your email</li>
              <li>✅ Access to Customer Portal</li>
            </ul>
          `
        },
        {
          title: 'Managing Your Subscription',
          content: `
            <h3 class="font-bold text-lg mb-2">Customer Portal</h3>
            
            <h4 class="font-bold mt-4 mb-2">Access Your Portal:</h4>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Go to <strong>Billing</strong> page</li>
              <li>Click <strong>"Manage Subscription"</strong></li>
              <li>Customer portal opens</li>
            </ol>
            
            <h4 class="font-bold mt-4 mb-2">What You Can Do:</h4>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>View invoices</li>
              <li>Update payment method</li>
              <li>Change plan (upgrade/downgrade)</li>
              <li>Switch billing frequency (monthly ↔ annual)</li>
              <li>Update billing address</li>
              <li>Download receipts</li>
              <li>Cancel subscription</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">How to Cancel:</h4>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Open Customer Portal</li>
              <li>Click "Cancel subscription"</li>
              <li>Confirm cancellation</li>
              <li>Your subscription ends at period end</li>
            </ol>
            
            <div class="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 mt-4">
              💡 No refunds for partial months, but you keep access until your billing period ends.
            </div>
          `
        }
      ]
    },
    'troubleshooting': {
      title: 'Troubleshooting',
      icon: HelpCircle,
      articles: [
        {
          title: 'Leads Not Being Detected',
          content: `
            <h3 class="font-bold text-lg mb-2">Why Aren't Leads Showing Up?</h3>
            
            <h4 class="font-bold mt-4 mb-2">Check These First:</h4>
            
            <p class="font-semibold mt-2">1. Email Connection Status</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Go to Channels page</li>
              <li>Is your email showing "Connected ✅"?</li>
              <li>If expired, reconnect</li>
            </ul>
            
            <p class="font-semibold mt-2">2. Email Provider Settings</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Gmail: App password must be current</li>
              <li>Check spam/junk folder - move OffTrigger to inbox</li>
              <li>IMAP must be enabled</li>
            </ul>
            
            <p class="font-semibold mt-2">3. Platform Support</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>We support 35+ platforms</li>
              <li>New/unknown platforms may not be detected yet</li>
              <li>Contact support to add new platforms</li>
            </ul>
            
            <p class="font-semibold mt-2">4. Email Format</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Some platforms send leads in unique formats</li>
              <li>Forward a sample lead to support@offtrigger.com</li>
              <li>We'll add support within 24 hours</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">Test Lead Detection:</h4>
            <p class="mb-2">Send yourself a test email with:</p>
            <pre class="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto text-sm mb-4">
Subject: Property inquiry - 123 Main Street

Hi, I'm interested in the property at 123 Main Street.

My name is John Smith
Email: john@example.com  
Phone: 555-1234

Please contact me!
            </pre>
            
            <p class="mb-4">This should be detected within 30 seconds.</p>
          `
        },
        {
          title: 'Auto-Responses Not Sending',
          content: `
            <h3 class="font-bold text-lg mb-2">Why Aren't Responses Going Out?</h3>
            
            <h4 class="font-bold mt-4 mb-2">Common Causes:</h4>
            
            <p class="font-semibold mt-2">1. Email Services Down</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Check our Status page: status.offtrigger.com</li>
              <li>Email services rotate: Resend → Brevo → Gmail</li>
              <li>Usually resolves within minutes</li>
            </ul>
            
            <p class="font-semibold mt-2">2. Lead Missing Contact Info</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Auto-response requires email or phone</li>
              <li>Check lead details - is email/phone present?</li>
              <li>If missing, respond manually</li>
            </ul>
            
            <p class="font-semibold mt-2">3. Template Not Saved</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Go to Templates page</li>
              <li>Is your template saved?</li>
              <li>Click "Save" after editing</li>
            </ul>
            
            <p class="font-semibold mt-2">4. Trial/Subscription Expired</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Check Billing page</li>
              <li>If expired, upgrade to resume</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">Check Response Status:</h4>
            <ol class="list-decimal pl-5 space-y-1 mb-4">
              <li>Open lead in Dashboard</li>
              <li>Check "Response Sent" field</li>
              <li>Shows timestamp if sent</li>
              <li>Shows error if failed</li>
            </ol>
          `
        },
        {
          title: 'Common Error Messages',
          content: `
            <h3 class="font-bold text-lg mb-2">Error Messages & Solutions</h3>
            
            <h4 class="font-bold mt-4 mb-1 text-red-500">"Authentication failed"</h4>
            <p class="mb-1"><strong>Cause:</strong> Wrong email/password</p>
            <p class="mb-1 text-sm text-gray-500 font-semibold">Fix:</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Double-check credentials</li>
              <li>For Gmail: Use App Password, not regular password</li>
              <li>For Outlook: Use App Password</li>
              <li>Try generating new app password</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-1 text-red-500">"Connection timeout"</h4>
            <p class="mb-1"><strong>Cause:</strong> Network or server issue</p>
            <p class="mb-1 text-sm text-gray-500 font-semibold">Fix:</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Check your internet connection</li>
              <li>Try again in 5 minutes</li>
              <li>Email provider servers may be down</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-1 text-red-500">"Invalid IMAP settings"</h4>
            <p class="mb-1"><strong>Cause:</strong> Wrong server/port</p>
            <p class="mb-1 text-sm text-gray-500 font-semibold">Fix:</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Gmail: imap.gmail.com, port 993</li>
              <li>Outlook: outlook.office365.com, port 993</li>
              <li>Contact your email provider for correct settings</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-1 text-red-500">"Email limit exceeded"</h4>
            <p class="mb-1"><strong>Cause:</strong> Hit your plan's lead limit</p>
            <p class="mb-1 text-sm text-gray-500 font-semibold">Fix:</p>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Check Billing page for current usage</li>
              <li>Upgrade to higher plan if needed</li>
              <li>Starter: 100 leads/month</li>
              <li>Professional: 300 leads/month</li>
              <li>Team: Unlimited</li>
            </ul>
          `
        }
      ]
    },
    'supported-platforms': {
      title: 'Supported Platforms',
      icon: Globe,
      articles: [
        {
          title: 'Complete Platform List',
          content: `
            <h3 class="font-bold text-lg mb-2">35+ Platforms Supported Worldwide</h3>
            
            <h4 class="font-bold mt-4 mb-2">🇺🇸 United States</h4>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Zillow</li>
              <li>Redfin</li>
              <li>Realtor.com</li>
              <li>Trulia</li>
              <li>Homes.com</li>
              <li>RE/MAX</li>
              <li>Coldwell Banker</li>
              <li>Century 21</li>
              <li>Keller Williams</li>
              <li>& more (Check Lead Sources Page)</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">🇬🇧 United Kingdom</h4>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Rightmove</li>
              <li>Zoopla</li>
              <li>OnTheMarket</li>
              <li>PrimeLocation</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">🇮🇳 India</h4>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>99acres</li>
              <li>MagicBricks</li>
              <li>Housing.com</li>
              <li>NoBroker</li>
              <li>CommonFloor</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">🇦🇪 UAE</h4>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Property Finder</li>
              <li>Bayut</li>
              <li>Dubizzle</li>
            </ul>
            
            <h4 class="font-bold mt-4 mb-2">🇦🇺 Australia</h4>
            <ul class="list-disc pl-5 space-y-1 mb-4">
              <li>Domain</li>
              <li>Realestate.com.au</li>
            </ul>
            
            <div class="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 mt-4">
              💡 Don't see your platform? Email us at support@offtrigger.com with a sample lead email. We'll add support within 24 hours!
            </div>
          `
        }
      ]
    }
  };

  // Filter logic
  const filteredSections = Object.entries(sections).map(([key, section]) => {
    const matchingArticles = section.articles.filter((article) =>
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.content.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return {
      ...section,
      key,
      articles: matchingArticles
    };
  }).filter(section => section.articles.length > 0);

  const IconActive = sections[activeSection]?.icon || HelpCircle;

  return (
    <div className={`min-h-screen pb-12 text-left ${isDark ? 'bg-navy' : 'bg-mint'}`}>
      <div className="mb-10">
        <h2 className={`text-3xl font-bold mb-2 ${isDark ? 'text-light' : 'text-darkTeal'}`}>Help & Support</h2>
        <p className={`text-lg ${isDark ? 'text-light/60' : 'text-darkTeal/60'}`}>Everything you need to know about OffTrigger.</p>
      </div>

      {/* Quick Action Cards - Horizontal Scroll on Mobile */}
      <div className="flex flex-nowrap md:grid md:grid-cols-3 gap-4 md:gap-5 mb-8 overflow-x-auto md:overflow-visible pb-4 md:pb-0 snap-x snap-mandatory -mx-6 px-6 md:mx-0 md:px-0 no-scrollbar">
        {/* Contact Support */}
        <div className={`snap-center min-w-[75vw] md:min-w-0 flex-shrink-0 rounded-xl p-5 border transition-all duration-300 group shadow-lg ${isDark ? 'bg-navy border-light/10 hover:border-blue-400' : 'bg-white border-teal/10 hover:border-blue-500'}`}>
          <div className={`w-11 h-11 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform bg-blue-500/10 text-blue-500`}><Headphones size={22} /></div>
          <h3 className={`text-lg font-bold mb-1 transition-colors ${isDark ? 'text-light group-hover:text-blue-400' : 'text-darkTeal group-hover:text-blue-500'}`}>Contact Support</h3>
          <p className={`text-xs mb-4 ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>Get help from our team. 24h response.</p>
          <a href="mailto:support@offtrigger.com" className={`block w-full text-center py-2 rounded-lg border font-bold transition-all text-xs uppercase tracking-wide border-blue-500/30 text-blue-500 hover:bg-blue-500 hover:text-white group-hover:bg-blue-500 group-hover:text-white`}>Email Us</a>
        </div>

        {/* Feature Request */}
        <div className={`snap-center min-w-[75vw] md:min-w-0 flex-shrink-0 rounded-xl p-5 border transition-all duration-300 group shadow-lg ${isDark ? 'bg-navy border-light/10 hover:border-amber-400' : 'bg-white border-teal/10 hover:border-amber-500'}`}>
          <div className={`w-11 h-11 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform bg-amber-500/10 text-amber-500`}><Lightbulb size={22} /></div>
          <h3 className={`text-lg font-bold mb-1 transition-colors ${isDark ? 'text-light group-hover:text-amber-400' : 'text-darkTeal group-hover:text-amber-500'}`}>Feature Request</h3>
          <p className={`text-xs mb-4 ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>Suggest new features you'd love.</p>
          <a href="mailto:support@offtrigger.com?subject=Feature%20Request" className={`block w-full text-center py-2 rounded-lg border font-bold transition-all text-xs uppercase tracking-wide border-amber-500/30 text-amber-500 hover:bg-amber-500 hover:text-white group-hover:bg-amber-500 group-hover:text-white`}>Submit Idea</a>
        </div>

        {/* Report a Bug */}
        <div className={`snap-center min-w-[75vw] md:min-w-0 flex-shrink-0 rounded-xl p-5 border transition-all duration-300 group shadow-lg ${isDark ? 'bg-navy border-light/10 hover:border-pink' : 'bg-white border-teal/10 hover:border-pink'}`}>
          <div className={`w-11 h-11 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform ${isDark ? 'bg-pink/10 text-pink' : 'bg-pink/10 text-pink'}`}><Bug size={22} /></div>
          <h3 className={`text-lg font-bold mb-1 transition-colors ${isDark ? 'text-light group-hover:text-pink' : 'text-darkTeal group-hover:text-pink'}`}>Report a Bug</h3>
          <p className={`text-xs mb-4 ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>Found an issue? Let us know.</p>
          <a href="mailto:support@offtrigger.com?subject=Bug%20Report" className={`block w-full text-center py-2 rounded-lg border font-bold transition-all text-xs uppercase tracking-wide ${isDark ? 'border-pink/30 text-pink hover:bg-pink hover:text-white group-hover:bg-pink group-hover:text-white' : 'border-pink/30 text-pink hover:bg-pink hover:text-white group-hover:bg-pink group-hover:text-white'}`}>Report Bug</a>
        </div>
      </div>

      <div className="mb-8 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search size={20} className={isDark ? "text-light/40" : "text-darkTeal/40"} />
        </div>
        <input
          type="text"
          placeholder="Search guides, tutorials, and help articles..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-cyan focus:border-cyan transition-colors ${isDark
            ? 'bg-navy border-light/10 text-light placeholder-light/40 focus:bg-navy'
            : 'bg-white border-teal/10 text-darkTeal placeholder-darkTeal/40 focus:bg-white'
            }`}
        />
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        {/* Navigation Sidebar */}
        <div className="w-full md:w-64 flex-shrink-0">
          <div className="sticky top-24 space-y-6">
            <div className="space-y-1">
              {Object.entries(sections).map(([key, section]) => {
                const Icon = section.icon;
                const isActive = activeSection === key;
                return (
                  <button
                    key={key}
                    onClick={() => setActiveSection(key)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors font-medium border ${isActive
                      ? isDark
                        ? 'bg-cyan/10 text-cyan border-cyan/20'
                        : 'bg-primary/10 text-primary border-primary/20'
                      : isDark
                        ? 'text-light hover:bg-light/5 border-transparent'
                        : 'text-darkTeal hover:bg-teal/5 border-transparent'
                      }`}
                  >
                    <Icon size={18} />
                    {section.title}
                  </button>
                );
              })}
            </div>


          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1">
          {searchTerm ? (
            <div>
              <h3 className={`text-xl font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-light' : 'text-darkTeal'}`}>
                <Search size={24} className={isDark ? 'text-cyan' : 'text-primary'} />
                Search Results for "{searchTerm}"
              </h3>
              {filteredSections.length > 0 ? (
                <div className="space-y-6">
                  {filteredSections.map(section => (
                    <div key={section.key} className="space-y-4">
                      <h4 className={`text-sm font-semibold uppercase tracking-wider ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>
                        {section.title}
                      </h4>
                      {section.articles.map((article, index) => {
                        const articleId = `${section.key}-${index}`;
                        const isOpen = openArticles[articleId];
                        return (
                          <AccordionItem
                            key={articleId}
                            id={articleId}
                            title={article.title}
                            isOpen={isOpen}
                            onToggle={toggleArticle}
                            isDark={isDark}
                          >
                            <div dangerouslySetInnerHTML={{ __html: article.content }} />
                          </AccordionItem>
                        );
                      })}
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`text-center py-12 border rounded-lg ${isDark ? 'border-light/10 text-light/60 bg-navy' : 'border-teal/10 text-darkTeal/60 bg-white'}`}>
                  <HelpCircle size={48} className={`mx-auto mb-4 opacity-50 ${isDark ? 'text-light' : 'text-darkTeal'}`} />
                  <p className="text-lg font-medium">No results found for "{searchTerm}"</p>
                  <p className="mt-2 text-sm">Try adjusting your search terms or checking different categories.</p>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center border shadow-sm ${isDark ? 'bg-cyan/10 border-cyan/20 text-cyan' : 'bg-primary/10 border-primary/20 text-primary'
                  }`}>
                  <IconActive size={20} />
                </div>
                <h3 className={`text-2xl font-bold ${isDark ? 'text-light' : 'text-darkTeal'}`}>
                  {sections[activeSection]?.title}
                </h3>
              </div>
              <div className="space-y-4">
                {sections[activeSection]?.articles.map((article, index) => {
                  const articleId = `${activeSection}-${index}`;
                  const isOpen = openArticles[articleId];
                  return (
                    <AccordionItem
                      key={articleId}
                      id={articleId}
                      title={article.title}
                      isOpen={isOpen}
                      onToggle={toggleArticle}
                      isDark={isDark}
                    >
                      <div className="article-content" dangerouslySetInnerHTML={{ __html: article.content }} />
                    </AccordionItem>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Roadmap & Limitations Section */}
      <div className={`mt-12 rounded-xl border shadow-lg relative overflow-hidden ${isDark ? 'bg-navy border-light/10' : 'bg-white border-teal/10'}`}>
        <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${isDark ? 'from-pink to-cyan' : 'from-pink to-primary'}`}></div>
        <div className="p-6 md:p-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="material-symbols-outlined text-pink">construction</span>
            <h3 className={`text-xl font-bold ${isDark ? 'text-light' : 'text-darkTeal'}`}>Roadmap & What's Coming</h3>
          </div>
          <p className={`text-sm mb-6 ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>We believe in transparency. Here's what we're working on.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex gap-3 items-start">
              <div className={`mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0 shadow-[0_0_5px] ${isDark ? 'bg-cyan shadow-cyan' : 'bg-primary shadow-primary'}`}></div>
              <div>
                <h4 className={`text-sm font-bold ${isDark ? 'text-light' : 'text-darkTeal'}`}>Email Providers</h4>
                <ul className={`text-xs space-y-1 mt-1 ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>
                  <li>• Currently: Gmail, Outlook, IMAP</li>
                  <li>• Coming: Google Workspace</li>
                </ul>
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <div className="mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0 bg-yellow-500 shadow-[0_0_5px_#EAB308]"></div>
              <div>
                <h4 className={`text-sm font-bold ${isDark ? 'text-light' : 'text-darkTeal'}`}>Platform Coverage</h4>
                <ul className={`text-xs space-y-1 mt-1 ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>
                  <li>• Some platforms mask contact info</li>
                  <li>• Chrome extension coming Q2 2026</li>
                </ul>
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <div className="mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0 bg-pink shadow-[0_0_5px_#FF2E63]"></div>
              <div>
                <h4 className={`text-sm font-bold ${isDark ? 'text-light' : 'text-darkTeal'}`}>Two-Way Messaging</h4>
                <ul className={`text-xs space-y-1 mt-1 ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>
                  <li>• Currently: Replies go to inbox</li>
                  <li>• Coming Q1 2026: In-app messaging</li>
                </ul>
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <div className={`mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0 shadow-[0_0_5px] ${isDark ? 'bg-cyan shadow-cyan' : 'bg-primary shadow-primary'}`}></div>
              <div>
                <h4 className={`text-sm font-bold ${isDark ? 'text-light' : 'text-darkTeal'}`}>Team Features</h4>
                <ul className={`text-xs space-y-1 mt-1 ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>
                  <li>• Currently: Solo agent accounts</li>
                  <li>• Coming Q2 2026: Team accounts</li>
                </ul>
              </div>
            </div>
          </div>
          <p className={`text-xs mt-6 ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>Have a suggestion? Email us at <a href="mailto:support@offtrigger.com" className={`font-semibold hover:underline ${isDark ? 'text-cyan' : 'text-primary'}`}>support@offtrigger.com</a> — we prioritize based on demand.</p>
        </div>
      </div>

      {/* Contact Support Banner */}
      <div className={`mt-8 rounded-xl border p-8 text-center shadow-lg ${isDark
        ? 'bg-gradient-to-br from-navy to-gray-800 border-cyan/30 shadow-[0_0_20px_rgba(8,217,214,0.05)]'
        : 'bg-gradient-to-br from-white to-gray-50 border-primary/30 shadow-[0_0_20px_rgba(8,217,214,0.1)]'
        }`}>
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDark ? 'bg-cyan/10 text-cyan' : 'bg-primary/10 text-primary'}`}>
          <Headphones size={32} />
        </div>
        <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-light' : 'text-darkTeal'}`}>Still need help?</h3>
        <p className={`text-sm mb-4 ${isDark ? 'text-light/60' : 'text-darkTeal/60'}`}>Our team is here to help you succeed. Reach out anytime.</p>
        <a href="mailto:support@offtrigger.com" className={`font-bold text-lg hover:underline mb-3 block ${isDark ? 'text-cyan' : 'text-primary'}`}>support@offtrigger.com</a>
        <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 px-3 py-1 rounded-full">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          <span className="text-green-500 text-xs font-bold uppercase tracking-wider">24h Response Guarantee</span>
        </div>
      </div>

      {/* Footer */}
      <div className={`mt-12 mb-4 flex items-center justify-between text-xs border-t pt-6 ${isDark ? 'text-light/30 border-light/5' : 'text-darkTeal/30 border-teal/5'}`}>
        <p>© 2026 OffTrigger.</p>
        <div className="flex gap-4">
          <a className="hover:text-cyan transition-colors" href="#">Privacy</a>
          <a className="hover:text-cyan transition-colors" href="#">Terms</a>
          <a className="hover:text-cyan transition-colors" href="#">System Status</a>
        </div>
      </div>
    </div>
  );
};

const AccordionItem = ({ id, title, isOpen, onToggle, isDark, children }) => (
  <div className={`border rounded-lg overflow-hidden transition-all duration-200 shadow-sm ${isDark ? 'bg-navy border-light/10 hover:border-cyan/30' : 'bg-white border-teal/10 hover:border-primary/30'}`}>
    <button
      onClick={() => onToggle(id)}
      className={`w-full flex items-center justify-between p-4 md:p-5 cursor-pointer transition-colors ${isDark ? 'hover:bg-light/5 text-light' : 'hover:bg-teal/5 text-darkTeal'}`}
    >
      <span className="font-semibold text-left text-base md:text-lg pr-4">{title}</span>
      <span className={`material-symbols-outlined transition-transform duration-300 transform ${isOpen ? 'rotate-180' : ''} ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>
        {isOpen ? 'remove' : 'add'}
      </span>
    </button>
    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'}`}>
      <div className={`px-4 md:px-5 pb-5 text-sm md:text-base border-t leading-relaxed ${isDark ? 'text-light/80 bg-black/20 border-light/5' : 'text-darkTeal/80 bg-teal/5 border-teal/5'}`}>
        <div className="pt-4 prose prose-sm md:prose-base max-w-none dark:prose-invert">
          {children}
        </div>
      </div>
    </div>
  </div>
);

export default Help;
